import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  DxButtonModule,
  DxDataGridModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTemplateModule,
  DxTextBoxModule,
} from "devextreme-angular";
import { ContactsAddCompanyInlineComponent } from "../contacts-add-company-inline/contacts-add-company-inline.component";
import { ContactsAddPersonInlineComponent } from "../contacts-add-person-inline/contacts-add-person-inline.component";
import { ContactService } from "src/app/services/contact.service";
import { ConfigService } from "src/app/services/config.service";
import { AppGlobals } from "../../app.globals";
import { ValueChangedEvent } from "devextreme/ui/tag_box";
import { AppModule } from "src/app/app.module";
import { GpdButtonComponent } from "../gpd-button/gpd-button.component";
import { GpdRoleSelectComponent } from "../gpd-role-select/gpd-role-select.component";

import { SearchableDropdownDirective } from '../../directives/searchable-dropdown.directive';
@Component({
    selector: "app-contacts-popup",
    imports: [
        CommonModule,
        DxPopupModule,
        DxDataGridModule,
        DxButtonModule,
        DxTextBoxModule,
        ContactsAddCompanyInlineComponent,
        ContactsAddPersonInlineComponent,
        DxTagBoxModule,
        DxTemplateModule,
        GpdButtonComponent,
        GpdRoleSelectComponent,
        SearchableDropdownDirective,
    ],
    templateUrl: "./contacts-popup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./contacts-popup.component.css"]
})
export class ContactsPopupComponent implements OnInit {
  @ViewChild("companyGrid") companyGrid;
  @ViewChild("personGrid") personGrid;
  @ViewChild(ContactsAddCompanyInlineComponent) addCompanyForm?: ContactsAddCompanyInlineComponent;
  @ViewChild(ContactsAddPersonInlineComponent) addPersonForm?: ContactsAddPersonInlineComponent;

  @Input("visible") visible = false;
  @Input("contactType") contactType: "company" | "person" | "all" = "all";
  @Input() tagFilter: boolean = false;
  @Input() title?: string;
  @Input() excludeContactId?: number;
  @Input() excludeContactType?: "company" | "person";
  // Off by default — this popup is used in ~10 places across the app (task assignees, cost
  // entries, property owners, etc.) where "role" has no meaning. Only project-add-contact-modal
  // (the "Kontakt hinzufügen" flow) turns this on, since only there does creating the contact
  // inline also mean immediately assigning it a role for that project.
  @Input() showRole: boolean = false;
  // Bound into the projected role block below (see popup-role in the template) — kept here
  // rather than on the inline form itself since it's this popup's own concern, not the
  // create-person/company form's.
  role: string = '';

  editMode = false;
  openedDirectlyInEditMode = false;
  popupSelectedTab = 0;

  @Input() companies = new Array();
  @Input() persons = new Array();
  contactTags: any;
  selectedTags: any[] = [];

  @Output() onContactSelected = new EventEmitter();
  @Output() onContactAdded = new EventEmitter();
  @Output() onHiding = new EventEmitter();

  searchValueCompany: string;
  filtersCompany: any;

  searchValuePerson: string;
  searchFilterPerson: any;

  tagFilterValues: any;

  // While creating a new contact inline, the header names what's actually being created
  // ("Person erstellen"/"Firma erstellen") instead of staying on the generic picker title.
  get popupTitle(): string {
    if (this.editMode) {
      return this.popupSelectedTab === 1
        ? $localize`:@@createPerson:Person erstellen`
        : $localize`:@@createCompany:Firma erstellen`;
    }
    return this.title || $localize`:@@addContact:Kontakt hinzufügen`;
  }

  constructor(
    private contactService: ContactService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.getPageData();
    if (this.contactType == "company") {
      this.popupSelectedTab = 0;
    } else if (this.contactType == "person") {
      this.popupSelectedTab = 1;
    }
  }

  getPageData() {
    this.configService.getContactTags({}).then(
      (result) => {
        if (result.hasError === true)
          AppGlobals.ShowErrorMessage(result.feedback);
        else {
          this.contactTags = result.contactTags;
        }
      },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      }
    );
  }

  handleContactSelected(e, type) {
    if (this.isExcludedContact(e.data, type)) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@ownerAlreadySelected:This contact is already the current property owner.`
      );
      return;
    }
    this.getPageData();
    this.onContactSelected.emit({ data: e.data, type: type });
    this.editMode = false;
    this.visible = false;
  }

  isExcludedContact(data, type): boolean {
    return (
      this.excludeContactId != null &&
      data?.id === this.excludeContactId &&
      type === this.excludeContactType
    );
  }

  onRowPrepared(e, type) {
    if (e.rowType === "data" && this.isExcludedContact(e.data, type)) {
      e.rowElement.classList.add("disabled-row");
    }
  }

  handleNewContactSelected(e, type) {
    this.editMode = false;
    if(type == 'person'){
      this.persons.push(e.data);
    }else if(type == 'company'){
      this.companies.push(e.data);
    }
    this.onContactAdded.emit({
      id: e.data.id,
      type: type,
      displayName: e.data.displayName,
      role: this.role,
    });
    this.role = '';
  }

  // Bound instead of a plain "editMode = true" in the template so a role typed for a
  // previous (aborted or completed) creation doesn't linger into the next one.
  startCreating(): void {
    this.editMode = true;
    this.role = '';
  }
  filterValueChange($event) {}
  filterTags(filterValue, selectedFilterOperation) {
    if (!filterValue || filterValue.length === 0) {
      return null;
    }

    return function (item) {
      if (!item.tags.includes(filterValue)) {
        return false;
      }
      return true;
    };
  }

  handleAbort(e?) {
    if (this.openedDirectlyInEditMode) {
      this.visible = false;
    } else {
      this.editMode = false;
    }
  }

  submitInlineForm() {
    if (this.popupSelectedTab === 0) {
      this.addCompanyForm?.handleSubmitButton(null);
    } else {
      this.addPersonForm?.handleSubmitButton(null);
    }
  }

  handleOnHiding() {
    this.popupSelectedTab = 0;
    this.onHiding.emit();
  }

  onPopupHidden() {
    this.editMode = false;
    this.openedDirectlyInEditMode = false;
  }

  getTag(item) {
    let tag = this.contactTags.find((tag) => tag.id == item);
    if (tag) {
      return tag.title;
    } else {
      return "";
    }
  }

  tagValueChangedCompany(e: ValueChangedEvent) {
    this.selectedTags = e.value;
    this.companyGrid.instance.filter(function (item) {
      for (let tag of e.value) {
        if (!item.tags.includes(tag)) {
          return false;
        }
      }
      return true;
    });
  }
  tagValueChangedPerson(e: ValueChangedEvent) {
    this.selectedTags = e.value;
    this.personGrid.instance.filter(function (item) {
      for (let tag of e.value) {
        if (!item.tags.includes(tag)) {
          return false;
        }
      }
      return true;
    });
  }

  // searchValueChangedPerson(e) {
  //   this.searchValuePerson = e.value;
  //   this.searchFilterPerson = [
  //     ['title', 'contains', this.searchValuePerson],
  //     'or',
  //     ['salutation', 'contains', this.searchValuePerson],
  //     'or',
  //     ['firstname', 'contains', this.searchValuePerson],
  //     'or',
  //     ['lastname', 'contains', this.searchValuePerson],
  //     'or',
  //     ['address', 'contains', this.searchValuePerson],
  //     'or',
  //     ['city', 'contains', this.searchValuePerson],
  //     'or',
  //     ['zipcode', 'contains', this.searchValuePerson]
  //   ];
  //   this.applyFilterPersonGrid();
  // }

  applyFilterCompanyGrid() {
    this.companyGrid.instance.filter([this.filtersCompany]);
  }

  applyFilterPersonGrid() {
    this.personGrid.instance.filter([this.searchFilterPerson]);
  }

  onCellPrepared(e) {
    if (this.searchValueCompany || this.searchFilterPerson) {
      let value = e.displayValue;
      //let titleLower = title.toLowerCase().split(this.searchValue).join(searchMatchString);

      let searchValue;
      if (this.popupSelectedTab == 0) {
        searchValue = this.searchValueCompany;
      }
      if (this.popupSelectedTab == 1) {
        searchValue = this.searchValuePerson;
      }

      let index = value.toLowerCase().indexOf(searchValue.toLowerCase());
      if (index != -1) {
        let match = value.slice(index, index + searchValue.length);
        var searchMatchString =
          "<span class='search-text'>" + match + "</span>";
        value = value.split(match).join(searchMatchString);
        var result = "<div>" + value + "</div>";
        if (e.cellElement) {
          e.cellElement.innerHTML = result;
        }
      }
    }
  }

  async filterContactsByTag(e: any) {
    console.log("tagFilterValues", this.tagFilterValues);
    if (this.tagFilterValues.length < 1) {
    }
    for (let i = 0; i < this.tagFilterValues.length; i++) {
      this.companyGrid.instance.filter([
        "id",
        "contains",
        this.tagFilterValues[i],
      ]);
      this.personGrid.instance.filter([
        "id",
        "contains",
        this.tagFilterValues[i],
      ]);
    }
    this.companyGrid.instance.refresh();
    this.personGrid.instance.refresh();
  }
}
